import { Spinner } from "react-bootstrap";
import {
  Localization,
  AppMessagesContext,
  actionSetProgressMessage,
  SettingsManager,
} from "@viamap/viamap2-common";
import "./PropertyInformationDialog.css";
import { Feature } from "src/states/ApplicationState";
import { ProtectedFeature } from "./ProtectedFeature";
import { PropertyInfoView } from "src/propertyInfoTemplates/PropertyInfoView";
import { FiCopy, FiPrinter } from "react-icons/fi";
import { MitStorage } from "src/managers/MitStorage";
import { ViewButtonCheck, ViewButtonDropdown, ViewButtonIcon } from "src/propertyInfoTemplates/PropertyComponents";
import { ExportData } from "src/managers/ExportData";
import { PropertyData } from "src/propertyInfoTemplates/PropertyInfoCollectorTypes";
import { SheetFunc } from "src/managers/SheetFunc";
import { ADModalBodyProp, AdvancedDragModal } from "src/componentsUtils/AdvancedDragModal";
import { useState, useContext, useEffect } from "react";
import { closeWindow, ManagedWindow, WindowId } from "src/WindowManager/WindowState";
import { useWindowContext } from "src/WindowManager/useWindowContext";
import { Document } from "iconsax-react";

type Props = {
  managedWindow: ManagedWindow;
  labelLatLng?: { latlng?: { lat: number; lng: number } };
  bfeNr?: number | string;
  ejerlavskode?: string;
  matrnr?: string;
  allowExtendedInformation: boolean;
  cols?: number;
};

export function PropertyInformationDialog(props: Props) {
  const [propertyDefaultCollapsed, setPropertyDefaultCollapsed] =
    useState(MitStorage.getValue<Boolean>("PropertyAutoColapsed"));
  const { dispatch: appMessageDispatch } = useContext(AppMessagesContext);
  const { dispatch: windowDispatch } = useWindowContext();
  const [isLoading, setIsLoading] = useState(true);
  const [propertyData, setPropertyData] = useState<any>();

  useEffect(() => {
    setIsLoading(true);
  }, [props.labelLatLng, props.bfeNr]);

  function RemberCollapseSettings(e: any, val) {
    MitStorage.setValue<Boolean>("PropertyAutoColapsed", !val);
  }

  async function handleExportToExcel() {
    let wb = await (new ExportData()).createPropertyDataExcelExport(
      "Property Export",
      new Date(),
      propertyData,
      (progress: number) => {
        appMessageDispatch(actionSetProgressMessage("Export", Math.round(100 * progress / 1)));
      });
    let blob = SheetFunc.createBlob(wb);
    let fileName = "PropertyData" + (propertyData?.BBRRel?.bfeNummer || "");
    fileName += ".xlsx";
    ExportData.downloadBlob(fileName, blob);
  }

  let fold = (
    <ViewButtonCheck
      checked={!propertyDefaultCollapsed}
      className="no-print"
      onCheck={(e, val) => {
        RemberCollapseSettings(e, val);
        setPropertyDefaultCollapsed(val);
      }}
    >
      {Localization.getText("Expanded")}
    </ViewButtonCheck>
  );

  return (
    <AdvancedDragModal
      title={"Ejendom"}
      window={props.managedWindow}
      allowUserMiniMize={true}
      allowFullscreen={true}
      PosDefault={{ width: "800px", height: "calc(100% - 50px)", top: "50px", left: "50%", transform: "translateX(-50%)" }}
      PosDefaultNS={{ width: "calc( 100% - 30px )", height: "calc(100% - 50px)", top: "50px", left: "15px" }}
      saveKey="PropertyInfo"
      PositionToSave={{ width: true, height: true, top: true, left: true }}
      variant="NSPadding"
      onClose={() => {
        windowDispatch(closeWindow(WindowId.PropertyInformationDialog))
      }}
      subHeaderContent={
        <>
          <ProtectedFeature
            feature={Feature.LinkToMunicipalityWebDocuments}
            contentsIfNoAccess={<></>}
          >
            <ViewButtonDropdown hideEmpty className="ViewButtonDropdownIcon" onSelect={(e) => {
              window.open(e, "_blank");
            }} options={function () {
              let muni = parseInt(propertyData?.DARAdresser?.flat()?.[0]?.husnummer.kommuneinddeling.kommunekode ?? "9999");
              return SettingsManager.getSystemSetting("propertyExternalMunicipalityDocuments")?.filter((a) => {
                return a.municipality.includes(muni);
              }).map((a) => {
                return { text: a.name, value: a.url };
              }) || []
            }()}
            >
              <Document size="24" />
            </ViewButtonDropdown>
          </ProtectedFeature>
          <ProtectedFeature feature={Feature.ExplorerToExcel} contentsIfNoAccess={<></>} >
            {isLoading ? <></>
              :
              <ViewButtonIcon
                disabled={false}
                title={Localization.getText("Export to Excel")} className="no-print" onClick={() => handleExportToExcel()} ><img src="./images/NewSecSVG/excel.svg"></img></ViewButtonIcon>
            }
          </ProtectedFeature>
          <ProtectedFeature
            feature={Feature.OpenPropetyInfoDocument}
            contentsIfNoAccess={<></>}
          >

            <ViewButtonIcon
              className="no-print"
              onClick={() =>
                window.open(
                  `/propertyinfo/?bfeNr=${props.bfeNr}`,
                  "_blank"
                )
              }
              title={Localization.getText("Popout")}
            >
              <FiCopy />
            </ViewButtonIcon>
          </ProtectedFeature>
          <ProtectedFeature
            feature={Feature.PrintPropertyInfo}
            contentsIfNoAccess={<></>}
          >
            <>
              {!(isLoading) ? (
                <>
                  <ViewButtonIcon
                    className="no-print"
                    onClick={() => {
                      setTimeout(() => {
                        print();
                      }, 100)
                    }}
                    title={Localization.getText("Print")}
                  >
                    <FiPrinter />
                  </ViewButtonIcon>
                </>
              ) : (
                <Spinner size="sm" />
              )}
            </>
          </ProtectedFeature>
          {fold}
        </>
      }
    >
      {/* <div className="mit-printable-modal" > */}
      {/* <PrintHeader/> */}
      <ADModalBodyProp>
        {(props.bfeNr && (
          <PropertyInfoView
            key={props.bfeNr?.toString() || ""}
            foldetUd={!propertyDefaultCollapsed}
            cols={1}
            bfeNr={props.bfeNr?.toString() || ""}
            ejerlavskode={props.ejerlavskode?.toString() || ""}
            matrnr={props.matrnr?.toString() || ""}
            callBackOnLoaded={(data: PropertyData) => {
              setIsLoading(false);
              setPropertyData(data);
            }}
            allowExtendedInformation={true}
          />
        )) ||
          "Missing BFE number"}
      </ADModalBodyProp>
    </AdvancedDragModal>
  );
}
